import { useSubmit } from "@certn/api";
import { showToast, Stack } from "@certn/cascadia";
import { Container, Text as StepHeadingText } from "@certn/cascadia-react";
import {
  EVENT_NAMES,
  useEventTracking,
  useTimeTracking,
  RegionContext,
  RegionProvider,
} from "@certn/utils";
import { css } from "@emotion/css";
import { useLDClient, useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReviewStepDailyPay } from "./ReviewStepDailyPay";
import { ReviewStepApplicantPay } from "./ReviewStepApplicantPay";
import { useOrderBillingProfile } from "./useOrderBillingProfile";
import { useOrderBlockingProfile } from "./useOrderBlockingProfile";
import { OrderBlockedMessage } from "../components/OrderBlockedMessage";
import {
  PlaceOrderDisabledReasons,
  getPlaceOrderDisabledReasons,
} from "../components/PlaceOrderDisabledReasons";
import { OrderReviewEmailPreview } from "../../../components/ordering-review/OrderReviewEmailPreview";
import {
  OrderReviewSummaryBox,
  isMissingCardOnFile,
} from "../../../components/ordering-review/OrderReviewSummaryBox";
import { WwccLimitationsMessage } from "../../../components/ordering-review/WwccLimitationsMessage";
import { ConsumerReportCertificationSection } from "../../../components/ordering-review/ConsumerReportCertificationSection";
import { useUser } from "../../../hooks/useUser";
import { OrderContext } from "../OrderContext";
import { clearOrderDraft, useOrderDraftKey } from "../orderDraftStorage";
import { OrderStepLayout } from "../components/OrderStepLayout";
import {
  enabledNotificationChannels,
  orderContextToSubmitOrderPayload,
  orderIncludesAuWwcc,
  orderRequiresEmploymentConsumerReportCertification,
  showSubmitOrderError,
} from "../order-utils";
import { useCanOrderCVReview } from "../../../components/cv-review/utils";
import { CVReviewExtraction } from "../../../components/cv-review/CVReviewExtraction";
import { useFocusStepHeading } from "../useFocusStepHeading";

export function ReviewStep() {
  const { data } = useOrderBillingProfile();

  if (
    data?.invoicing_frequency === "DAILY" &&
    data?.payer_option === "CLIENT"
  ) {
    return <ReviewStepDailyPay />;
  }

  if (data?.payer_option === "APPLICANT") {
    return <ReviewStepApplicantPay />;
  }

  return <ReviewStepInvoice billingProfile={data} />;
}

function ReviewStepInvoice({
  billingProfile,
}: {
  billingProfile: ReturnType<typeof useOrderBillingProfile>["data"];
}) {
  const { order, goToNextStep } = useContext(OrderContext);
  const { t } = useTranslation();
  const stepHeadingId = useFocusStepHeading();
  const { details: userDetails } = useUser();
  const orderDraftKey = useOrderDraftKey();
  const { trackEvent } = useEventTracking();
  const [disableCompletion, setDisableCompletion] = useState<boolean>(false);
  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/submit-order/`,
    {
      throwErrors: true,
    }
  );
  const { startTimeTracker, stopTimeTracker } = useTimeTracking();
  const ldClient = useLDClient();

  const { canOrderCVReview, templateId, verificationType } =
    useCanOrderCVReview({
      regularChecks: order.checks,
      packageChecks: order.packageData?.checks || [],
    });

  const {
    billingRequireCardOnFileForOrder,
    applicantSmsMessaging,
    applicantWhatsappMessaging,
  } = useFlags();
  const { isBlocked, blockingProfile } = useOrderBlockingProfile();
  const [
    consumerReportCertificationAcknowledged,
    setConsumerReportCertificationAcknowledged,
  ] = useState(false);
  const certificationRequired =
    orderRequiresEmploymentConsumerReportCertification(order, userDetails);

  useEffect(() => {
    startTimeTracker("order--review-step");
  }, [startTimeTracker]);

  const submitOrder = async () => {
    try {
      await submit(
        orderContextToSubmitOrderPayload(order, userDetails, {
          consumerReportCertificationAcknowledged:
            certificationRequired && consumerReportCertificationAcknowledged,
          enabledNotificationChannels: enabledNotificationChannels({
            applicantSmsMessaging,
            applicantWhatsappMessaging,
          }),
        })
      );
      trackEvent(EVENT_NAMES.CP_SUBMITTED_PAYMENT, {
        paymentMethod: "invoice",
        checksOrdered: order.checks.map((c) => c.key).join(","),
      });
      stopTimeTracker("order--review-step");
      stopTimeTracker("order--order-complete");
      ldClient?.track(
        "client-portal--order--order-details-step--average-number-of-recipients-per-order",
        order.recipients.length,
        order.recipients.length
      );
      if (order.message) {
        ldClient?.track(
          "client-portal--order--order-complete--additional-message-usage-rates"
        );
      }

      clearOrderDraft(orderDraftKey);

      showToast(t("Order submitted"), { type: "info" });
      goToNextStep();
    } catch (e) {
      showSubmitOrderError(e, t);
    }
  };

  const missingCardOnFile = isMissingCardOnFile(
    billingProfile,
    !!billingRequireCardOnFileForOrder
  );

  const disabledReasons = getPlaceOrderDisabledReasons({
    hasNoRecipients: order.recipients.length === 0,
    isMissingCardOnFile: missingCardOnFile,
    isOrderBlocked: isBlocked,
    isCertificationUnacknowledged:
      certificationRequired && !consumerReportCertificationAcknowledged,
    isCvReviewInvalid: disableCompletion,
  });
  return (
    <OrderStepLayout
      onComplete={submitOrder}
      disableCompletion={disabledReasons.length > 0}
      submitting={isSubmitting}
      scrollRegionAriaLabel={t("Order review content")}
    >
      <Container layout="wide">
        <StepHeadingText
          id={stepHeadingId}
          as="h1"
          tabIndex={-1}
          className={styles.titleSpacing}
          style="global/title-small"
        >
          {t("Review and send")}
        </StepHeadingText>
        {isBlocked && blockingProfile && (
          <OrderBlockedMessage profile={blockingProfile} />
        )}

        <div className={styles.mainContentContainer}>
          <Stack className={styles.emailColumn} gap="space-6">
            <PlaceOrderDisabledReasons reasons={disabledReasons} />
            {orderIncludesAuWwcc(order) && <WwccLimitationsMessage />}
            {certificationRequired && (
              <ConsumerReportCertificationSection
                acknowledged={consumerReportCertificationAcknowledged}
                onAcknowledgedChange={
                  setConsumerReportCertificationAcknowledged
                }
              />
            )}
            {canOrderCVReview && order.cv_review_id && (
              <CVReviewExtraction
                cv_review_id={order.cv_review_id}
                templateId={templateId}
                verificationType={verificationType}
                setDisableCompletion={setDisableCompletion}
              />
            )}
            <OrderReviewEmailPreview order={order} />
          </Stack>
          <RegionProvider>
            <RegionContext.Consumer>
              {(region) => (
                <OrderReviewSummaryBox order={order} region={region} />
              )}
            </RegionContext.Consumer>
          </RegionProvider>
        </div>
      </Container>
    </OrderStepLayout>
  );
}

const styles = {
  titleSpacing: css({
    paddingTop: "var(--space-6)",
  }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-6)",
    flexWrap: "wrap-reverse",
  }),
  emailColumn: css({
    flex: "1 1 540px",
    minWidth: 0,
  }),
};
